import { useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Container,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconCircleCheck, IconInfoCircle } from '@tabler/icons-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPasswordSchema } from '@expeditor/shared';
import { authApi } from '../../shared/api/auth';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Пароли не совпадают');
      return;
    }
    const parsed = resetPasswordSchema.safeParse({ token, newPassword: password });
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => i.message).join('; '));
      return;
    }
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сменить пароль');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Container size="xs" py="xl">
        <Paper withBorder shadow="sm" p="lg" radius="md">
          <Alert color="red" icon={<IconInfoCircle size={16} />}>
            Ссылка некорректна (нет токена). Запросите сброс пароля заново.
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xs" py="xl">
      <Title order={2} ta="center" mb="md">
        Новый пароль
      </Title>
      <Paper withBorder shadow="sm" p="lg" radius="md">
        {done ? (
          <Stack>
            <Alert color="green" icon={<IconCircleCheck size={20} />} title="Пароль изменён">
              Все ваши активные сессии завершены — войдите с новым паролем.
            </Alert>
            <Button onClick={() => nav('/login', { replace: true })} fullWidth>
              Перейти к входу
            </Button>
          </Stack>
        ) : (
          <form onSubmit={onSubmit}>
            <Stack>
              <Text size="sm" c="dimmed">
                Требования: минимум 8 символов, заглавная буква, цифра, спецсимвол.
              </Text>
              <PasswordInput
                label="Новый пароль"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                autoComplete="new-password"
              />
              <PasswordInput
                label="Повторите пароль"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.currentTarget.value)}
                autoComplete="new-password"
              />
              {error && (
                <Alert color="red" icon={<IconInfoCircle size={16} />}>
                  {error}
                </Alert>
              )}
              <Button type="submit" loading={submitting} fullWidth>
                Сменить пароль
              </Button>
              <Anchor component={Link} to="/login" ta="center" size="sm">
                Отмена
              </Anchor>
            </Stack>
          </form>
        )}
      </Paper>
    </Container>
  );
}
